import { Text as T, useTranslator } from '@eo-locale/preact';
import ImageIcon from 'images/icons/Image.svg';
import { useCallback } from 'preact/hooks';
import { useEffect } from 'react';
import { useToggle } from 'utilities/use-toggle';

import { Button } from 'components/Button';
import { Checkbox } from 'components/Checkbox';
import { FormLayout } from 'components/FormLayout';
import { Image } from 'components/Image';
import { InlineError } from 'components/InlineError';
import { Modal } from 'components/Modal';
import { useModal } from 'components/ModalProvider';
import { useSettings } from 'components/SettingsProvider';
import { Stack } from 'components/Stack';
import { Stars } from 'components/Stars';
import { Text } from 'components/Text';
import { TextField } from 'components/TextField';

import { ImageUpload } from '../ImageUpload';
import { PrivacyPolicyIFrame } from '../PrivacyPolicyIFrame';

import { useForm } from './utilities/use-form';

import styles from './AddReviewModal.module.scss';

export function AddReviewModal() {
  const { translate } = useTranslator();
  const { settings, utils } = useSettings();
  const { open, closeModal } = useModal();
  const {
    fields: {
      rating,
      nickname,
      email,
      title,
      review,
      privacy_policy,
      images,
      pros,
      cons,
    },
    submitted,
    submitError,
    submit,
    reset,
    submitting,
  } = useForm();
  const { product } = utils;

  const {
    hide_review_title,
    allow_image_upload,
    enable_pros_cons,
    show_privacy_policy,
  } = settings.reviews.review_form;

  const handleClose = useCallback(() => {
    reset();
    closeModal();
  }, [reset, closeModal]);

  const {
    value: openPrivacy,
    setFalse: closePrivacyModal,
    setTrue: openPrivacyModal,
  } = useToggle(false);

  useEffect(() => {
    if (!open) return;

    const event = new CustomEvent('wbtxWriteReviewOpened', {
      bubbles: false,
    });

    document.dispatchEvent(event);
  }, [open]);

  if (product === null) {
    return null;
  }

  const productPreviewMarkup = (
    <Stack wrap={false} alignment="center">
      <div className={styles.ProductImage}>
        <Image
          saveRatio
          blurBackground
          source={product.image_src || ImageIcon}
          alt={product.title}
        />
      </div>
      <Stack.Item fill>
        <Text size="small" variation="semiStrong">
          {product.title}
        </Text>
      </Stack.Item>
    </Stack>
  );

  const submitErrorsMarkup = submitError && (
    <Stack vertical>
      <InlineError fieldId="">
        <T
          id=""
          defaultMessage="An error occurred while submitting the review"
        />
      </InlineError>
    </Stack>
  );

  const titleFieldMarkup = !hide_review_title && (
    <TextField
      required
      label={translate('write_review_summary_label', {
        defaultMessage: 'Review Title',
      })}
      placeholder={translate('write_review_summary_placeholder', {
        defaultMessage: 'Give your review a title',
      })}
      error={title.error}
      value={title.value}
      onChange={title.onChange}
    />
  );

  const imageUploadMarkup = allow_image_upload && (
    <ImageUpload field={images} />
  );

  const prosConsFieldsMarkup = enable_pros_cons && [
    <TextField
      key="pros"
      multiline={4}
      label={translate('write_review_pros_label', {
        defaultMessage: 'Pros',
      })}
      maxLength={200}
      value={pros.value}
      onChange={pros.onChange}
    />,
    <TextField
      key="cons"
      multiline={4}
      label={translate('write_review_cons_label', {
        defaultMessage: 'Cons',
      })}
      maxLength={200}
      value={cons.value}
      onChange={cons.onChange}
    />,
  ];

  const privacyPolicyMarkup = show_privacy_policy && (
    <>
      <Checkbox
        required
        label={
          <Text size="small">
            <T
              id="write_review_privacy_policy_label"
              defaultMessage="I have read and accept the Privacy Policy"
            />
          </Text>
        }
        error={privacy_policy.error}
        checked={privacy_policy.checked}
        onChange={privacy_policy.onChange}
      />
      <Text size="small">
        <Button plain onClick={openPrivacyModal}>
          <T
            id="write_review_privacy_policy_link"
            defaultMessage="Privacy policy"
          />
        </Button>
      </Text>
      <Modal
        large
        priority={1}
        title={translate('write_review_privacy_policy_link', {
          defaultMessage: 'Privacy Policy',
        })}
        open={openPrivacy}
        onClose={closePrivacyModal}
      >
        <PrivacyPolicyIFrame />
      </Modal>
    </>
  );

  const modalContent = submitted ? (
    <div style={{ padding: '32px 0 50px 0' }}>
      <Stack vertical alignment="center">
        <Text as="h2" variation="strong" size="extraLarge">
          <T id="write_review_thank_you_label" defaultMessage="Thank you!" />
        </Text>
        <Text variation="subdued" size="small">
          <T id="write_review_thank_you_message" defaultMessage="Thank you" />
        </Text>
      </Stack>
    </div>
  ) : (
    <FormLayout>
      {productPreviewMarkup}
      <FormLayout.Row>
        <Stack vertical spacing="tight">
          <Text size="small" variation="semiStrong">
            <T id="write_review_rating_stars_label" defaultMessage="Rating" />
          </Text>
          <Stars
            error={rating.error}
            value={rating.value}
            onChange={rating.onChange}
          />
        </Stack>
      </FormLayout.Row>
      <FormLayout.Row>
        <TextField
          required
          label={translate('write_review_nickname_label', {
            defaultMessage: 'Nickname',
          })}
          placeholder={translate('write_review_nickname_placeholder', {
            defaultMessage: 'Enter your first name',
          })}
          error={nickname.error}
          value={nickname.value}
          onChange={nickname.onChange}
        />
        <TextField
          required
          autocomplete="email"
          type="email"
          label={translate('write_review_email_label', {
            defaultMessage: 'Email',
          })}
          placeholder={translate('write_review_email_placeholder', {
            defaultMessage: 'Enter your email',
          })}
          error={email.error}
          value={email.value}
          onChange={email.onChange}
        />
      </FormLayout.Row>
      {titleFieldMarkup}
      <TextField
        required
        showCharacterCount
        multiline={7}
        label={translate('write_review_review_label', {
          defaultMessage: 'Review',
        })}
        placeholder={translate('write_review_review_placeholder', {
          defaultMessage: 'Enter your feedback',
        })}
        maxLength={2000}
        error={review.error}
        value={review.value}
        onChange={review.onChange}
      />
      {prosConsFieldsMarkup}
      {privacyPolicyMarkup}
      {imageUploadMarkup}
      <Button primary loading={submitting} onClick={submit}>
        <T id="write_review_submit_button" defaultMessage="Submit" />
      </Button>
      {submitErrorsMarkup}
    </FormLayout>
  );

  return (
    <Modal
      title={
        !submitted
          ? translate('write_review_title', {
              defaultMessage: 'Write a review',
            })
          : undefined
      }
      open={open}
      classNames={['wbtxReviews__modal-addReview']}
      onClose={handleClose}
    >
      {modalContent}
    </Modal>
  );
}
